// src/forms/EditHymnForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import toast from "react-hot-toast";

/** tiny local slugifier (no external hook so nothing “not found”) */
const slugify = (s = "") =>
  s
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]+/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

/** Convert hymn doc -> safe shape */
const safeHymn = (d) => ({
  title: d?.title || "",
  author_name: d?.author_name || "",
  key_label: d?.key_label || "",
  lyrics: d?.lyrics || "",
  master_number:
    typeof d?.master_number === "number"
      ? d.master_number
      : Number(d?.master_number || 0),
  title_slug: d?.title_slug || "",
});

export default function EditHymnForm() {
  const { hymnId } = useParams();
  const navigate = useNavigate();

  // base hymn
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hymn, setHymn] = useState(safeHymn({}));

  // lookups
  const [authorOptions, setAuthorOptions] = useState([]);
  const [keyOptions, setKeyOptions] = useState([]);
  const [allBooks, setAllBooks] = useState([]); // [{id,name}]

  // linked books (value) and per-book numbers
  const [selectedBooks, setSelectedBooks] = useState([]); // [{id,name}]
  const [bookNumbers, setBookNumbers] = useState({}); // bookId -> number

  // derive first line (for hymnbook_entries convenience)
  const firstLine = useMemo(() => {
    const t = (hymn?.lyrics || "").split(/\r?\n/).map((x) => x.trim());
    const first = t.find((x) => x.length);
    return first || hymn?.title || "";
  }, [hymn?.lyrics, hymn?.title]);

  /* ------------------- LOAD DATA ------------------- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // hymn
        const hsnap = await getDoc(doc(db, "hymns", hymnId));
        if (!hsnap.exists()) {
          toast.error("Hymn not found");
          navigate("/");
          return;
        }
        setHymn(safeHymn(hsnap.data()));

        // authors
        const aSnap = await getDocs(
          query(collection(db, "hymn_authors"), orderBy("name", "asc"))
        );
        setAuthorOptions(aSnap.docs.map((d) => d.data()?.name).filter(Boolean));

        // keys
        const kSnap = await getDocs(
          query(collection(db, "hymn_keys"), orderBy("label", "asc"))
        );
        setKeyOptions(kSnap.docs.map((d) => d.data()?.label).filter(Boolean));

        // books
        const bSnap = await getDocs(
          query(collection(db, "hymnbooks"), orderBy("name", "asc"))
        );
        const books = bSnap.docs.map((d) => ({ id: d.id, name: d.data()?.name || "" }));
        setAllBooks(books);

        // current links
        const linkSnap = await getDocs(
          query(
            collection(db, "hymnbook_entries"),
            where("hymn_id", "==", hymnId)
          )
        );
        const sel = [];
        const mapNums = {};
        linkSnap.forEach((d) => {
          const row = d.data();
          if (!row?.book_id) return;
          const bookObj =
            books.find((b) => b.id === row.book_id) || {
              id: row.book_id,
              name: row.book_name || "",
            };
          sel.push(bookObj);
          mapNums[row.book_id] =
            typeof row.hymn_number === "number"
              ? row.hymn_number
              : Number(row.hymn_number || 0);
        });
        // de-dup by id
        const uniqueSel = sel.filter(
          (v, i, self) => i === self.findIndex((x) => x.id === v.id)
        );
        setSelectedBooks(uniqueSel);
        setBookNumbers((prev) => ({ ...prev, ...mapNums }));
      } catch (e) {
        console.error(e);
        toast.error("Failed to load hymn data");
      } finally {
        setLoading(false);
      }
    })();
  }, [hymnId, navigate]);

  /* ------------------- UI HANDLERS ------------------- */
  const updateField = (k) => (e) => {
    const v = typeof e === "string" ? e : e?.target?.value;
    setHymn((h) => ({ ...h, [k]: v }));
  };

  const setBookNumber = (bookId, v) => {
    const n = Number(v || 0);
    setBookNumbers((m) => ({ ...m, [bookId]: n }));
  };

  const handleAddAuthor = async () => {
    const name = (hymn?.author_name || "").trim();
    if (!name) return;
    try {
      await setDoc(doc(collection(db, "hymn_authors")), {
        name,
        createdAt: serverTimestamp(),
      });
      if (!authorOptions.includes(name)) {
        setAuthorOptions((a) => [...a, name].sort((x, y) => x.localeCompare(y)));
      }
      toast.success("Author added");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add author");
    }
  };

  const handleAddKey = async () => {
    const label = (hymn?.key_label || "").trim();
    if (!label) return;
    try {
      await setDoc(doc(collection(db, "hymn_keys")), {
        label,
        createdAt: serverTimestamp(),
      });
      if (!keyOptions.includes(label)) {
        setKeyOptions((a) => [...a, label].sort((x, y) => x.localeCompare(y)));
      }
      toast.success("Key added");
    } catch (e) {
      console.error(e);
      toast.error("Failed to add key");
    }
  };

  const resetForm = () => {
    // simply reload from Firestore to restore
    navigate(0);
  };

  /* ------------------- SAVE ------------------- */
  const handleSave = async () => {
    try {
      setSaving(true);

      // basic validation
      if (!hymn.title.trim()) {
        toast.error("Title is required");
        setSaving(false);
        return;
      }
      const title_slug = slugify(hymn.title);

      // 1) update hymn document
      await updateDoc(doc(db, "hymns", hymnId), {
        title: hymn.title.trim(),
        title_slug,
        author_name: hymn.author_name.trim(),
        key_label: hymn.key_label.trim(),
        lyrics: hymn.lyrics,
        master_number:
          typeof hymn.master_number === "number"
            ? hymn.master_number
            : Number(hymn.master_number || 0),
        updatedAt: serverTimestamp(),
      });

      // 2) sync hymnbook_entries
      const currentSnap = await getDocs(
        query(collection(db, "hymnbook_entries"), where("hymn_id", "==", hymnId))
      );
      const current = currentSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      const selectedIds = new Set(selectedBooks.map((b) => b.id));

      // (a) delete unselected
      await Promise.all(
        current
          .filter((c) => !selectedIds.has(c.book_id))
          .map((c) => deleteDoc(doc(db, "hymnbook_entries", c.id)))
      );

      // (b) upsert selected
      await Promise.all(
        selectedBooks.map(async (b) => {
          const id = `${b.id}_${hymnId}`; // stable id (as your db already uses)
          const hymn_number =
            typeof bookNumbers[b.id] === "number"
              ? bookNumbers[b.id]
              : Number(bookNumbers[b.id] || hymn.master_number || 0);

          await setDoc(doc(db, "hymnbook_entries", id), {
            book_id: b.id,
            book_name: b.name,
            hymn_id: hymnId,
            hymn_title: hymn.title.trim() || firstLine,
            hymn_number,
            sort_order: hymn_number,
            createdAt: serverTimestamp(),
          });
        })
      );

      toast.success("Hymn updated");
      navigate("/");
    } catch (e) {
      console.error(e);
      toast.error("Save failed. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------- RENDER ------------------- */
  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 1.5 }}>Loading hymn for editing…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: "auto" }}>
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Edit Hymn
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          Update details and linked hymnbooks.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <TextField
            label="Master Number"
            type="number"
            value={hymn.master_number}
            onChange={updateField("master_number")}
            fullWidth
          />

          <TextField
            label="Hymn Title"
            value={hymn.title}
            onChange={updateField("title")}
            fullWidth
          />

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                freeSolo
                options={authorOptions}
                value={hymn.author_name || ""}
                onInputChange={(_, v) => setHymn((h) => ({ ...h, author_name: v }))}
                renderInput={(params) => (
                  <TextField {...params} label="Author Name" />
                )}
              />
              <Tooltip title="Add author to list">
                <IconButton size="small" onClick={handleAddAuthor} sx={{ mt: 0.5 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Autocomplete
                freeSolo
                options={keyOptions}
                value={hymn.key_label || ""}
                onInputChange={(_, v) => setHymn((h) => ({ ...h, key_label: v }))}
                renderInput={(params) => <TextField {...params} label="Song Key" />}
              />
              <Tooltip title="Add key to list">
                <IconButton size="small" onClick={handleAddKey} sx={{ mt: 0.5 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>

          <TextField
            label="Lyrics (Plain Text)"
            value={hymn.lyrics}
            onChange={updateField("lyrics")}
            fullWidth
            minRows={8}
            multiline
          />

          <Divider sx={{ my: 1 }} />

          {/* Link to Hymnbooks */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Link to Hymnbooks
            </Typography>

            <Autocomplete
              multiple
              options={allBooks}
              getOptionLabel={(o) => o?.name || ""}
              value={selectedBooks}
              onChange={(_, val) => {
                setSelectedBooks(val || []);
                // seed numbers for any newly selected with either existing or master #
                const add = {};
                (val || []).forEach((b) => {
                  if (bookNumbers[b.id] == null) {
                    add[b.id] =
                      typeof hymn.master_number === "number"
                        ? hymn.master_number
                        : Number(hymn.master_number || 0);
                  }
                });
                if (Object.keys(add).length) {
                  setBookNumbers((m) => ({ ...m, ...add }));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Hymnbooks" placeholder="Hymnbooks" />
              )}
            />

            {/* Per-book hymn numbers */}
            {selectedBooks.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.75 }}>
                  Set the hymn number in each book:
                </Typography>
                <Stack spacing={1.2}>
                  {selectedBooks.map((b) => (
                    <Stack
                      key={b.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                    >
                      <Typography sx={{ minWidth: 240 }}>{b.name}</Typography>
                      <TextField
                        size="small"
                        type="number"
                        label="Hymn #"
                        value={bookNumbers[b.id] ?? ""}
                        onChange={(e) => setBookNumber(b.id, e.target.value)}
                        sx={{ width: 140 }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Update Hymn
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={resetForm}
              disabled={saving}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
